import { cloneElement, forwardRef, isValidElement, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cx } from './cx';

/**
 * Form primitives. Inputs use a >=16px font (SPEC §4: anything smaller triggers
 * iOS focus-zoom) and a consistent border + accent focus ring. `Field` wraps a
 * control with its label / hint / error in the tight rhythm AND wires the
 * accessibility relationships (id, aria-invalid, aria-describedby) so the team
 * never has to thread them by hand.
 *
 * ERROR STATE is driven by `aria-invalid` on the control (the one attribute that
 * works for the <Input> primitive AND a native <select>/PasswordField). The
 * control styling reacts to it via the `aria-invalid:` Tailwind variant in
 * `controlClass`, so an invalid field gets a danger border + danger focus ring
 * with zero extra wiring. `Field error="..."` flips it automatically.
 *
 * `controlClass` is exported so the rare control that CANNOT be the <Input>
 * primitive (a component like PasswordField, or a native <select>) can opt into
 * the identical styling instead of hand-rolling its own.
 */

// Shared visual base for every form control - border, surface, font, focus +
// invalid states. Vertical sizing is intentionally NOT here: single-line
// controls take a fixed height (controlClass) while the textarea grows.
const controlBase = cx(
  'w-full rounded-sm border border-border bg-surface px-3',
  'font-body text-[1rem] text-text',
  'placeholder:text-text-faint',
  'transition-[border-color,box-shadow] duration-150',
  'hover:not-disabled:border-text-muted',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent focus-visible:border-transparent',
  // Invalid: danger border at rest + on hover, danger focus ring (the
  // aria-invalid attribute selector out-specifies the accent rules above).
  // Arbitrary variant: aria-invalid is not a built-in Tailwind aria-* variant.
  'aria-[invalid=true]:border-danger aria-[invalid=true]:hover:not-disabled:border-danger',
  'aria-[invalid=true]:focus-visible:outline-danger',
  'disabled:opacity-50 disabled:cursor-not-allowed',
);

/**
 * Single-line control styling (Input, native <select>, PasswordField). Fixed at
 * 35px so every text field, dropdown, and the md <Button> share ONE height - a
 * row of fields + its Save button reads as one consistent system. (35px is the
 * md Button's content-driven height: 13px text + p-2.5 + 1px border.)
 */
export const controlClass = cx(controlBase, 'h-[35px] leading-none');

type InputProps = InputHTMLAttributes<HTMLInputElement> & { error?: boolean };
export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input({ className, error, ...rest }, ref) {
    return (
      <input
        ref={ref}
        aria-invalid={error || undefined}
        className={cx(controlClass, className)}
        {...rest}
      />
    );
  },
);

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: boolean };
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ className, error, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        aria-invalid={error || undefined}
        // The textarea opts out of the fixed single-line height: it keeps a
        // 4-line (96px) minimum and grows with its content (resize-y +
        // host-side auto-grow), so multi-line bios fit without scrolling.
        className={cx(controlBase, 'py-2 leading-normal min-h-[96px] resize-y', className)}
        {...rest}
      />
    );
  },
);

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & { error?: boolean };
/**
 * Native <select> with the shared control styling + a branded chevron (the OS
 * arrow is hidden via `.ds-select` in ds.css, which also reserves room for it
 * and the correct padding). Same 35px height as Input/Button.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select({ className, error, children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        aria-invalid={error || undefined}
        className={cx(controlClass, 'ds-select', className)}
        {...rest}
      >
        {children}
      </select>
    );
  },
);

export function Field({
  label,
  htmlFor,
  hint,
  error,
  required = false,
  optional = false,
  className,
  children,
}: {
  label?: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  /** Show a required marker on the label. */
  required?: boolean;
  /** Show an "(optional)" marker on the label. */
  optional?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const autoId = useId();
  const id = htmlFor ?? autoId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  // Wire id / aria-invalid / aria-describedby onto the control automatically so
  // the label associates and screen readers announce the hint + error. The
  // control keeps any of these it set itself.
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: (children.props as Record<string, unknown>).id ?? id,
        'aria-invalid':
          error ? true : (children.props as Record<string, unknown>)['aria-invalid'],
        'aria-describedby':
          cx(
            (children.props as Record<string, unknown>)['aria-describedby'] as string | undefined,
            describedBy,
          ) || undefined,
      })
    : children;

  return (
    <div className={cx('flex flex-col gap-2', className)}>
      {label && (
        <label
          htmlFor={id}
          className="flex items-center gap-1.5 font-body text-label font-semibold uppercase tracking-[0.04em] text-text-muted"
        >
          {label}
          {required && (
            <span className="text-danger" aria-hidden="true">
              *
            </span>
          )}
          {optional && !required && (
            <span className="font-body text-caption font-normal normal-case tracking-normal text-text-faint">
              optional
            </span>
          )}
        </label>
      )}
      {control}
      {hint && (
        <span id={hintId} className="font-body text-caption text-text-faint">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="font-body text-caption text-danger">
          {error}
        </span>
      )}
    </div>
  );
}
